import { RestHandler, MockedRequest, DefaultRequestBody, rest } from 'msw';
import { CreateClientQueryRequest } from '../../queries/use-create-client-query';
import { CreateClientQueryReplyRequest } from '../../queries/use-create-client-query-reply';
import { UpdateClientQueriesRequest } from '../../queries/use-update-client-queries';
import { UpdateClientQueryReplyRequest } from '../../queries/use-update-client-query-reply';
import { PostResponseBody } from '../../types/api-response';
import { ClientQuery } from '../../types/client-query';
import { OtherSchedule, OtherScheduleStatusEnum } from '../../types/other-schedule';
import { Reply } from '../../types/reply';
import { ReconciliationWorksheet, WorksheetStatusEnum } from '../../types/worksheet';
import { DB } from '../db';

const routes: RestHandler<MockedRequest<DefaultRequestBody>>[] = [];

routes.push(
    /**
     * GET /workpapers/:id/client-queries
     * Gets all client queries for a workpaper
     */
    rest.get('/workpapers/:workpaperId/client-queries', (req, res, ctx) => {
        const filter = req.url.searchParams.get('filter');
        const workpaperId = req.params.workpaperId as string;

        let clientQueries = DB.GetClientQueries(workpaperId);
        let totalItems = clientQueries.length;

        if (filter) {
            const filterObj = JSON.parse(decodeURIComponent(filter));

            if (filterObj.context) {
                clientQueries = clientQueries.filter(
                    (cq) => filterObj.context.includes(cq.worksheet?.id) || filterObj.context.includes(cq.otherSchedule?.id)
                );
            }
            totalItems = clientQueries.length;
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({ data: clientQueries, totalItems: totalItems }));
    }),

    /**
     * GET /workpapers/:workpaperId/client-queries/:id
     * Gets a client query for a workpaper
     */
    rest.get('/workpapers/:workpaperId/client-queries/:id', (req, res, ctx) => {
        const id = req.params.id as string;
        const clientQuery = DB.GetClientQuery(id);

        return res(ctx.delay(), ctx.status(200), ctx.json({ clientQuery }));
    }),

    /**
     * POST /workpapers/:workpaperId/client-queries
     * Creates a client query for a workpaper
     */
    rest.post<CreateClientQueryRequest>('/workpapers/:workpaperId/client-queries', (req, res, ctx) => {
        const workpaperId = req.params.workpaperId as string;

        const response: PostResponseBody<ClientQuery> = {
            hasErrors: false,
            results: [],
        };

        for (const { rawQuery, worksheetId } of req.body.clientQuery) {
            const clientQuery = DB.CreateClientQuery(workpaperId, rawQuery, worksheetId);
            response.results.push({ statusCode: 201, data: clientQuery });
        }

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    /**
     * PATCH /workpapers/:workpaperId/client-queries
     * Updates client queries for a workpaper
     */
    rest.patch<UpdateClientQueriesRequest>('/workpapers/:workpaperId/client-queries', (req, res, ctx) => {
        for (const clientQueryReq of req.body) {
            const query = DB.UpdateClientQuery(clientQueryReq.id, clientQueryReq.fields);

            if (!query) {
                continue;
            }

            if (query.worksheet) {
                const worksheet = DB.GetWorksheet(query.worksheet.id) as ReconciliationWorksheet;
                if (worksheet.status === WorksheetStatusEnum.REVIEWED) {
                    DB.CalculateWorksheetStatus(worksheet.id, WorksheetStatusEnum.AWAITING_REVIEW);
                }
            }
            if (query.otherSchedule) {
                const otherSchedule = DB.GetOtherSchedule(query.otherSchedule.id) as OtherSchedule;
                if (otherSchedule.status === OtherScheduleStatusEnum.REVIEWED) {
                    DB.CalculateOtherScheduleStatus(otherSchedule.id, OtherScheduleStatusEnum.AWAITING_REVIEW);
                }
            }
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({}));
    }),

    /**
     * DELETE /workpapers/:workpaperId/client-queries/:id
     * Deletes a client query for a workpaper
     */
    rest.delete('/workpapers/:workpaperId/client-queries/:id', (req, res, ctx) => {
        const id = req.params.id as string;

        DB.DeleteClientQuery(id);

        return res(ctx.delay(), ctx.status(204));
    }),

    /**
     * POST /workpapers/:workpaperId/client-queries/:id/replies
     * Create a reply for a client query
     */
    rest.post<CreateClientQueryReplyRequest>('/workpapers/:workpaperId/client-queries/:id/replies', (req, res, ctx) => {
        const clientQueryId = req.params.id as string;

        const response: PostResponseBody<Reply> = {
            hasErrors: false,
            results: [],
        };

        const clientQueryReply = DB.CreateClientQueryReply(clientQueryId, req.body.reply.rawReply);
        response.results.push({ statusCode: 201, data: clientQueryReply });

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    /**
     * PATCH /workpapers/:workpaperId/client-queries/:clientQueryId/replies/:id
     * Updates reply for a client query
     */
    rest.patch<UpdateClientQueryReplyRequest>('/workpapers/:workpaperId/client-queries/:clientQueryId/replies/:id', (req, res, ctx) => {
        const queryId = req.params.clientQueryId as string;
        const id = req.params.id as string;
        const { rawReply } = req.body;

        DB.UpdateClientQueryReply(queryId, id, rawReply);

        return res(ctx.delay(), ctx.status(200));
    }),

    /**
     * DELETE /workpapers/:workpaperId/client-queries/:clientQueryId/replies/:id
     * Deletes a reply for a client query
     */
    rest.delete('/workpapers/:workpaperId/client-queries/:clientQueryId/replies/:id', (req, res, ctx) => {
        const id = req.params.id as string;

        DB.DeleteClientQueryReply(id);

        return res(ctx.delay(), ctx.status(204));
    })
);

export { routes };
